import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Message from 'carbon-react/lib/components/message';
import Switch from 'carbon-react/lib/components/switch';
import Textbox from 'carbon-react/lib/components/textbox';
import Typography from 'carbon-react/lib/components/typography';

import { ExtendedWorkspaceListItem } from '@futrli/views/operations/workspaces/processors/workspacesList';

import { useWorkspaceName } from '../../../hooks/use-workspace-name/use-workspace-name';
import { useWorkspaceSettingsForm } from '../hooks/useWorkspaceSettingsForm';

import styles from './workspace-settings-form.scss';

interface WorkspaceSettingsFormProps {
  workspace?: ExtendedWorkspaceListItem;
  form: ReturnType<typeof useWorkspaceSettingsForm>;
  isCurrentDefaultWorkspace?: boolean;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDefaultWorkspaceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  revalidate?: () => void;
}

export const WorkspaceSettingsForm = ({
  workspace,
  form,
  isCurrentDefaultWorkspace,
  onNameChange,
  onDefaultWorkspaceChange,
  revalidate
}: WorkspaceSettingsFormProps) => {
  const intl = useIntl();
  const showWarning = isCurrentDefaultWorkspace && !form.isDefaultWorkspace;
  const workspaceName = useWorkspaceName(workspace);

  return (
    <Box>
      {workspace?.actions?.updateName && (
        <>
          <Typography mb={1} variant="span">
            <FormattedMessage id="workspaces.current_workspace_name" defaultMessage="Current workspace name" />
          </Typography>
          <Typography mb={0} fontSize="16px" fontWeight="500">
            {workspaceName}
          </Typography>
          <Textbox
            id="workspace-name"
            name="workspace-name"
            autoFocus
            label={intl.formatMessage({
              id: 'workspaces.new_workspace_name',
              defaultMessage: 'New workspace name'
            })}
            value={form.name}
            onChange={onNameChange}
            onBlur={revalidate}
            placeholder={intl.formatMessage({
              id: 'workspaces.enter_new_workspace_name',
              defaultMessage: 'Enter new workspace name'
            })}
            mt={2}
            error={form.errors.name?.message}
          />
        </>
      )}

      {workspace?.actions?.setAsDefault && (
        <>
          <Box className={styles.switchLabelFix} mt={2}>
            <Switch
              checked={form.isDefaultWorkspace}
              onChange={onDefaultWorkspaceChange}
              mb={0}
              mr={2}
              labelInline
              label={intl.formatMessage({
                id: 'workspaces.make_default_workspace',
                defaultMessage: 'Make default workspace'
              })}
            />
          </Box>

          {showWarning && (
            <Message showCloseIcon={false} open mt="12px">
              <FormattedMessage
                id="workspaces.settings_form.default_workspace_warning"
                defaultMessage="A default workspace makes finding organisations easier - we recommend you add your most used workspace as the default."
              />
            </Message>
          )}
        </>
      )}
    </Box>
  );
};
